/**
 * V2 map view — the collecting game board, shared by Discover, category,
 * and profile pages. Ported from V1 map.js: marker colors (green =
 * collected by you, orange = not yet, brown = signed out), clustering
 * past 20 markers, debounced bounds reporting, vintage styling.
 *
 * Degrades in plain words when the Maps JS key doesn't allow this origin
 * yet (see docs/V1-PARITY.md M3 note).
 */
import { escapeHtml } from '../../assets/js/utils.js';
import { slugify } from '../../assets/js/shared/slugify.js';
import { imageSrc } from '../core/photo.js';
import { getUrlParam, setUrlParams } from './share.js';
import { categoryIcon } from './icons.js';
import { isEmbed } from './embed.js';

const MARKER_COLORS = {
  collected: '#10b981',
  uncollected: '#f59e0b',
  default: '#795548',
  closed: '#9b9285', // faded ink — a place that's gone must not glow like a find
};

// No GPS answer, no saved viewport, no pins to frame → the WORLD, not
// Kansas: Yoink is international, and a visitor in Osaka who denies
// location should not open on the American plains (2026-08-04).
const WORLD_CENTER = { lat: 20, lng: 0 };
const WORLD_ZOOM = 2;
const DEFAULT_ZOOM = 4; // a saved center without a saved zoom
const CITY_ZOOM = 12;

export function mapsAvailable() {
  return Boolean(window.google?.maps?.Map);
}

// --- Maps key rejection ------------------------------------------------
// When Google rejects the key at auth time (website restriction missing
// this origin, invalid/expired key, billing), Maps JS greys the canvas
// with its own "Oops! Something went wrong" overlay and calls the global
// gm_authFailure — but it names the actual REASON only in a console.error
// (e.g. RefererNotAllowedMapError), and the user tests on an iPad with no
// console. So we catch the callback, sniff the reason code off
// console.error, and swap every mounted map for a plain-words diagnosis
// that includes it. Later mounts skip the dead canvas entirely.
let mapsAuthFailed = false;
let mapsErrorCode = '';
let mapsErrorRaw = '';
const liveHosts = new Set(); // healthy canvases, replaced on failure
const failedHosts = new Set(); // showing the diagnosis; re-render when the code arrives

// What each Google reason code means for THIS setup, in plain words.
const MAPS_ERROR_WORDS = {
  RefererNotAllowedMapError:
    'The key does not allow this website. In Google Cloud → Credentials → the maps key → Website restrictions, add v2.yoinkadventures.com/* (and yoinkadventures.com/*), or set Application restrictions to None.',
  InvalidKeyMapError: 'The key this site is using is not a valid Google Maps key.',
  ExpiredKeyMapError: 'The key has expired — issue a fresh one in Google Cloud.',
  ApiNotActivatedMapError:
    'The "Maps JavaScript API" is not enabled for this key. In Google Cloud → the key → API restrictions, make sure Maps JavaScript API is in the allowed list.',
  BillingNotEnabledMapError: 'Billing is switched off for this key’s Google Cloud project.',
  OverQuotaMapError: 'This key’s project has run out of its Google Maps quota for now.',
};

/** The tail of the key THIS page actually loaded — so an iPad screenshot
 * can be compared against Google Cloud → Credentials without a console. */
function servedKeyTail() {
  const script = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
  const match = script?.src.match(/[?&]key=([^&]+)/);
  return match ? match[1].slice(-6) : '';
}

function authFailHtml() {
  const why = MAPS_ERROR_WORDS[mapsErrorCode];
  const tail = servedKeyTail();
  return `
    <div class="v2-empty">Google declined the map key, so the map can’t
    draw. ${why ? escapeHtml(why) : 'If the key was recently restricted to specific websites, this address needs adding to its allowed list.'}
    Everything else keeps working.
    ${mapsErrorRaw ? `<br><small>Google’s exact words: ${escapeHtml(mapsErrorRaw)}</small>` : ''}
    ${tail ? `<br><small>This page is using the Maps key ending “…${escapeHtml(tail)}” — check that against Google Cloud → Credentials (V1 may be serving a different one).</small>` : ''}</div>`;
}

function showAuthFailure() {
  for (const host of liveHosts) failedHosts.add(host);
  liveHosts.clear();
  for (const host of failedHosts) host.innerHTML = authFailHtml();
}

// The reason code rides a console.error from Google's own code — often
// AFTER gm_authFailure fires. Wrap-and-pass-through to catch it either way.
const originalConsoleError = console.error.bind(console);
console.error = (...args) => {
  const text = args.map((a) => (typeof a === 'string' ? a : '')).join(' ');
  const match = text.match(/Google Maps JavaScript API (?:error|warning): (\w+)/);
  if (match) {
    mapsErrorCode = match[1];
    mapsErrorRaw = text.replace(/https?:\S+/g, '').replace(/\s+/g, ' ').trim().slice(0, 300);
    if (mapsAuthFailed) showAuthFailure(); // refresh with the named reason
  }
  originalConsoleError(...args);
};

window.gm_authFailure = () => {
  mapsAuthFailed = true;
  showAuthFailure();
};

/** Test hook: clears the remembered key failure between tests. */
export function _resetMapsAuthState() {
  mapsAuthFailed = false;
  mapsErrorCode = '';
  mapsErrorRaw = '';
  liveHosts.clear();
  failedHosts.clear();
}

/**
 * A short "where" line from a Google formatted address (map popups).
 * US addresses keep V1's "City, ST"; everywhere else reads
 * "City, Country" with postal codes and bare region abbreviations
 * stripped — "…, Tokyo 156-0041, Japan" is "Tokyo, Japan", not
 * "Tokyo 156-0041" (2026-08-04 on-device find: Yoink is international).
 * Exported for tests.
 */
export function extractCityState(formattedAddress) {
  if (!formattedAddress) return null;
  const parts = formattedAddress.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length < 3) return null;

  const last = parts[parts.length - 1];
  const isUS = last === 'USA' || last === 'United States';
  // A digit-free tail is a country name; a "CA 90042" / "Tokyo 156-0041"
  // tail means the country was omitted and the tail is region + postal.
  const hasCountry = isUS || !/\d/.test(last);
  const regionIdx = parts.length - (hasCountry ? 2 : 1);
  const region = parts[regionIdx] || '';
  const cityBefore = parts[regionIdx - 1] || '';

  if (isUS || !hasCountry) {
    // V1's US shape: "City, CA 90042[, USA]" → "City, CA"
    const st = region.match(/^([A-Z]{2})\b/);
    if (st && cityBefore) return `${cityBefore}, ${st[1]}`;
    return cityBefore && cityBefore.length < 50 ? cityBefore : null;
  }

  // International: the region slot reads "Tokyo 156-0041", "London SW1A
  // 2AA", "10117 Berlin", "Sydney NSW 2000"… — drop postal tokens and
  // 2-3 letter region codes; whatever remains is the city, else the
  // part before it ("Toronto, ON M5H 2N2, Canada" → Toronto).
  const cleaned = region
    .split(/\s+/)
    .filter((t) => !/\d/.test(t) && !/^[A-Z]{2,3}$/.test(t))
    .join(' ');
  const city = cleaned || cityBefore;
  if (!city || city.length >= 50) return null;
  return `${city}, ${last}`;
}

/**
 * Dedupe yoinks into marker locations with counts, the newest photo,
 * and the categories seen there (V1's rich popup inputs, map.js:420-506).
 * Exported for tests.
 */
export function deriveMapLocations(yoinks) {
  const byId = new Map();
  for (const y of yoinks) {
    const loc = y.locations;
    if (!loc?.id || loc.lat == null || loc.lng == null) continue;
    const entry = byId.get(loc.id) || { ...loc, yoinkCount: 0, recentPhoto: null, cats: [] };
    entry.yoinkCount += 1;
    if (!entry.recentPhoto) {
      const img = Array.isArray(y.images) ? y.images[0] : null;
      if (img) entry.recentPhoto = imageSrc(img);
    }
    const cat = y.categories;
    if (cat?.id && !entry.cats.some((c) => c.id === cat.id)) entry.cats.push(cat);
    byId.set(loc.id, entry);
  }
  // A visitor's photo always outranks Google's; the Google photo (rows
  // whose location select carried photo_url from the metadata JSONB)
  // keeps a never-yoinked pin from opening faceless.
  return [...byId.values()].map((entry) => ({
    ...entry,
    recentPhoto: entry.recentPhoto || entry.photo_url || null,
  }));
}

/**
 * Rows whose location falls inside a map viewport ({north,south,east,west}).
 * Rows without coordinates weren't on the map, so they don't make the cut.
 * Handles viewports that cross the antimeridian. The shared "map's view
 * carries back into the cards" filter (category, journal, Discover
 * following-scope). Exported for tests.
 */
export function rowsInArea(rows, area) {
  if (!area) return rows;
  return rows.filter((r) => {
    const lat = r.locations?.lat;
    const lng = r.locations?.lng;
    if (typeof lat !== 'number' || typeof lng !== 'number') return false;
    if (lat < area.south || lat > area.north) return false;
    return area.west <= area.east
      ? lng >= area.west && lng <= area.east
      : lng >= area.west || lng <= area.east;
  });
}

/**
 * The map viewport a shared URL carries (?lat/?lng/?zoom). Rejects
 * out-of-range garbage so a hand-mangled link can't strand the map.
 * Exported for tests.
 */
export function urlViewport() {
  const lat = parseFloat(getUrlParam('lat'));
  const lng = parseFloat(getUrlParam('lng'));
  const zoom = parseFloat(getUrlParam('zoom'));
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return {
    lat,
    lng,
    zoom: Number.isFinite(zoom) ? Math.min(Math.max(zoom, 1), 21) : null,
  };
}

/**
 * V1 color rule, plus the closed-place override: a permanently closed
 * place grays out for everyone — the orange "not yet collected" glow is
 * an invitation to drive there, which is exactly wrong for a place
 * that's gone. The pin stays (the yoinks are memories); the popup says
 * why it's gray. Exported for tests.
 */
export function markerColor(loc, collectedIds) {
  if (loc.status === 'closed_permanently') return MARKER_COLORS.closed;
  if (!collectedIds) return MARKER_COLORS.default;
  return collectedIds.has(loc.id) ? MARKER_COLORS.collected : MARKER_COLORS.uncollected;
}

/**
 * Breadcrumb: "this browser has granted location before". Written on
 * every successful fix; read by the header proximity light, which must
 * NEVER be the thing that pops the iOS permission dialog and so will
 * only look for a fix it knows will come silently.
 */
export const GEO_GRANTED_KEY = 'yoink-v2-geo-granted';

const geoGrantedListeners = new Set();

/**
 * Fires the first time a fix lands — the moment the breadcrumb above goes
 * from absent to set. On a browser that has never granted location, the
 * proximity light's gate is shut at boot and, throttled to five minutes,
 * would stay shut for the whole visit even though the feed's own locate
 * call succeeds seconds later. This is how it hears about that.
 * @returns {() => void} unsubscribe
 */
export function onGeoGranted(fn) {
  geoGrantedListeners.add(fn);
  return () => geoGrantedListeners.delete(fn);
}

export function markGeoGranted() {
  let first = true;
  try {
    first = localStorage.getItem(GEO_GRANTED_KEY) !== '1';
    localStorage.setItem(GEO_GRANTED_KEY, '1');
  } catch { /* private mode — no breadcrumb, so every fix counts as the first */ }
  if (!first) return;
  geoGrantedListeners.forEach((fn) => {
    try { fn(); } catch { /* one bad listener must not eat the fix */ }
  });
}

/**
 * One session's best-effort "where am I". A recent cached fix is fine
 * (maximumAge) and returns near-instantly — the map relies on this to zoom
 * to the user without a slow high-accuracy acquisition. Shared with
 * Discover so the grid scopes to the same place, the same fast way.
 * Resolves null when geolocation is unavailable or denied.
 */
export function getUserLocation() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        markGeoGranted();
        resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude });
      },
      () => resolve(null),
      { timeout: 8000, maximumAge: 300000 }
    );
  });
}

/**
 * One shared Map instance for the whole session. Every
 * `new google.maps.Map` is a BILLED map load, and the old
 * per-mount construction paid it again on every grid↔map toggle —
 * part of how the key hit OverQuotaMapError (2026-07-07). The canvas
 * div lives outside the DOM between mounts and gets re-parented in.
 * The constructor identity check rebuilds when the Maps object is
 * replaced (tests, SDK reload).
 */
let shared = null; // { canvas, map, infoWindow, ctor }

function obtainSharedMap(initialCenter, initialZoom) {
  if (shared && shared.ctor === google.maps.Map) return shared;
  const canvas = document.createElement('div');
  canvas.className = 'v2-map-canvas';
  const map = new google.maps.Map(canvas, {
    center: initialCenter,
    zoom: initialZoom,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    // 'auto' flips between one- and two-finger panning with page
    // scrollability, which reads as random on touch devices
    gestureHandling: 'greedy',
    styles: VINTAGE_STYLES,
  });
  // maxWidth caps the popup; without it Google sizes to content and
  // its own `.gm-style img{max-width:none!important}` blows the photo
  // up to fill it (iPad report 2026-07-08)
  shared = { canvas, map, infoWindow: new google.maps.InfoWindow({ maxWidth: 260 }), ctor: google.maps.Map };
  return shared;
}

/**
 * Mount a map into el.
 * @param {HTMLElement} el
 * @param {object} opts - { store, router, onBoundsChange?, fitToMarkers? }
 *   fitToMarkers: frame every marker on setYoinks (profile journals —
 *   V1 user-panel.js:1687-1692), zoom capped at 16 like V1.
 * @returns {{ setYoinks(yoinks, collectedIds), cleanup() }}
 *
 * In embed mode (?embed=1) the real mount waits until el scrolls into
 * the READER's view: an embed sits in someone else's page with someone
 * else's traffic, and constructing the map is what Google bills — a
 * frame below the fold of a popular post must not pay until a reader
 * actually reaches it. IntersectionObserver intersects against the
 * top-level viewport even from inside a cross-origin iframe, which is
 * exactly the "did the reader get here" question.
 */
export function mountMap(el, opts) {
  if (!isEmbed() || typeof IntersectionObserver !== 'function') return mountMapNow(el, opts);
  let inner = null;
  let pending = null; // last setYoinks args, replayed once the map exists
  let pendingPath = null; // ditto for the route line
  let destroyed = false;
  el.innerHTML = '<div class="v2-empty">The map is just ahead…</div>';
  const observer = new IntersectionObserver((entries) => {
    if (destroyed || inner || !entries.some((entry) => entry.isIntersecting)) return;
    observer.disconnect();
    el.innerHTML = '';
    inner = mountMapNow(el, opts);
    if (pending) inner.setYoinks(...pending);
    if (pendingPath) inner.setPath(pendingPath);
  }, { rootMargin: '200px' });
  observer.observe(el);
  return {
    setYoinks(...args) {
      pending = args;
      inner?.setYoinks(...args);
    },
    setPath(path) {
      pendingPath = path;
      inner?.setPath(path);
    },
    cleanup() {
      destroyed = true;
      observer.disconnect();
      inner?.cleanup();
      inner = null;
    },
  };
}

function mountMapNow(el, {
  store, router, onBoundsChange = null, fitToMarkers = false,
  // What a popup's count line calls its rows. Null OMITS the line: a
  // list's map plots saved entries, and counting those as "1 yoink"
  // contradicted the place page's honest 0 (on-device 2026-08-04).
  countNoun = 'yoink',
} = {}) {
  if (mapsAuthFailed) {
    el.innerHTML = authFailHtml();
    failedHosts.add(el); // keeps updating if the reason code arrives late
    return { setYoinks() {}, setPath() {}, cleanup() { failedHosts.delete(el); } };
  }
  if (!mapsAvailable()) {
    el.innerHTML = `
      <div class="v2-empty">The map isn’t available on this address yet —
      the Google Maps key needs v2.yoinkadventures.com added to its allowed
      websites. Everything else works meanwhile.</div>`;
    return { setYoinks() {}, setPath() {}, cleanup() {} };
  }

  liveHosts.add(el);
  el.innerHTML = '';
  let markers = [];
  let clusterer = null;
  let boundsTimer = null;
  let routeLine = null; // the "On the way" page's drawn route
  let destroyed = false;

  // A shared link's viewport outranks everything: the receiver must see
  // the exact map the sharer was looking at (user rule 2026-07-08) — so
  // it also suppresses the geolocation zoom and marker auto-framing.
  const fromUrl = urlViewport();
  if (fromUrl) fitToMarkers = false;
  const saved = fromUrl || store.get().mapCenter;
  const savedZoom = fromUrl?.zoom || store.get().mapZoom || (saved ? DEFAULT_ZOOM : WORLD_ZOOM);
  const { canvas, map, infoWindow } = obtainSharedMap(saved || WORLD_CENTER, savedZoom);
  el.appendChild(canvas);
  // V1's zoom-to-my-location control (map.js:623-663) — the only way
  // back to "here" once panned away. Overlay button, not the Maps
  // controls API, so it works everywhere including tests.
  const locateBtn = document.createElement('button');
  locateBtn.type = 'button';
  locateBtn.className = 'map-locate-btn';
  locateBtn.setAttribute('aria-label', 'Zoom to my location');
  locateBtn.textContent = '◎';
  locateBtn.addEventListener('click', async () => {
    locateBtn.disabled = true;
    const here = await getUserLocation();
    locateBtn.disabled = false;
    if (destroyed) return;
    if (!here) {
      locateBtn.textContent = '∅';
      setTimeout(() => { locateBtn.textContent = '◎'; }, 1500);
      return;
    }
    map.setCenter(here);
    map.setZoom(CITY_ZOOM);
  });
  el.appendChild(locateBtn);
  // A re-parented map needs a resize kick and its viewport re-asserted;
  // the idle trigger makes the map report its bounds through the normal
  // debounced path, so views need no eager (global) marker load.
  google.maps.event?.trigger?.(map, 'resize');
  google.maps.event?.trigger?.(map, 'idle');
  if (saved) {
    map.setCenter({ lat: saved.lat, lng: saved.lng });
    map.setZoom(savedZoom);
  } else if (!fitToMarkers) {
    getUserLocation().then((center) => {
      if (destroyed || !center) return;
      map.setCenter(center);
      map.setZoom(CITY_ZOOM);
    });
  }

  const idleListener = map.addListener('idle', () => {
    if (destroyed) return;
    const c = map.getCenter();
    // Write-on-CHANGE only: store.set notifies unconditionally, and
    // pages that re-render on store changes would remount this map,
    // whose idle would write again — the blinking remount loop the
    // on-device report caught (2026-07-07). Same guard for feedBounds
    // in discover.
    if (c) {
      const prev = store.get();
      const zoom = map.getZoom();
      const moved =
        !prev.mapCenter ||
        Math.abs(prev.mapCenter.lat - c.lat()) > 1e-7 ||
        Math.abs(prev.mapCenter.lng - c.lng()) > 1e-7 ||
        prev.mapZoom !== zoom;
      if (moved) store.set({ mapCenter: { lat: c.lat(), lng: c.lng() }, mapZoom: zoom });
      // Reflect the viewport into the URL on every idle (replaceState
      // notifies nothing, so no remount loop) so a share always carries
      // it — rounded (~1m precision) to keep the link presentable.
      setUrlParams({
        lat: String(+c.lat().toFixed(5)),
        lng: String(+c.lng().toFixed(5)),
        zoom: zoom == null ? '' : String(+zoom.toFixed(2)),
      });
    }
    if (!onBoundsChange) return;
    clearTimeout(boundsTimer);
    boundsTimer = setTimeout(() => {
      const b = map.getBounds();
      if (destroyed || !b) return;
      const ne = b.getNorthEast();
      const sw = b.getSouthWest();
      onBoundsChange({ north: ne.lat(), south: sw.lat(), east: ne.lng(), west: sw.lng() });
    }, 300);
  });

  // Info window "open place" buttons (delegated — the window renders
  // inside the map container)
  function onClick(event) {
    const go = event.target.closest('[data-map-goto]');
    if (go) {
      router.navigate('location', { id: go.dataset.mapGoto });
      return;
    }
    const catPill = event.target.closest('[data-map-cat]');
    if (catPill) router.navigate('category', { slug: catPill.dataset.mapCat });
  }
  el.addEventListener('click', onClick);

  function clearMarkers() {
    clusterer?.clearMarkers?.();
    clusterer = null;
    markers.forEach((m) => m.setMap(null));
    markers = [];
  }

  // V1's rich info window (map.js:420-506): newest photo, "City, ST",
  // count, up to 3 tappable category pills, then the door to the place.
  // Laid out HORIZONTALLY (photo beside text) since 2026-07-13: Google
  // caps the popup at the map's height and scroll-clips the overflow
  // with invisible scrollbars on iPad, so the stacked layout lost its
  // bottom half on the splash-height category map. The side-by-side
  // card stays under ~160px tall and fits every map we have.
  function infoHtml(loc) {
    const cityState = extractCityState(loc.formatted_address);
    const pills = (loc.cats || []).slice(0, 3);
    const overflow = (loc.cats || []).length - pills.length;
    const closed = loc.status === 'closed_permanently'
      ? '<div class="map-info-closed">Permanently closed</div>'
      : loc.status === 'closed_temporarily'
        ? '<div class="map-info-closed map-info-closed-temp">Temporarily closed</div>'
        : '';
    return `
      <div class="v2-map-info">
        <div class="map-info-top">
          ${loc.recentPhoto ? `<img class="map-info-photo" src="${loc.recentPhoto}" alt="" loading="lazy">` : ''}
          <div class="map-info-text">
            <strong>${escapeHtml(loc.display_name || 'Unknown place')}</strong>
            ${closed}
            ${cityState ? `<div class="map-info-city">${escapeHtml(cityState)}</div>` : ''}
            ${countNoun ? `<div>${loc.yoinkCount} ${countNoun}${loc.yoinkCount === 1 ? '' : 's'}</div>` : ''}
          </div>
        </div>
        ${pills.length ? `
        <div class="map-info-pills">
          ${pills.map((c) => `<button type="button" class="map-info-pill" data-map-cat="${slugify(c.name)}">${categoryIcon(c, 13)} ${escapeHtml(c.name)}</button>`).join('')}
          ${overflow > 0 ? `<span class="map-info-more">+${overflow} more</span>` : ''}
        </div>` : ''}
        <button type="button" class="v2-map-info-link" data-map-goto="${loc.id}">Open place</button>
      </div>`;
  }

  let openLocationId = null; // survives marker rebuilds (V1 map.js:128-136)
  infoWindow.addListener?.('closeclick', () => { openLocationId = null; });

  function setYoinks(yoinks, collectedIds = null) {
    if (destroyed) return;
    clearMarkers();
    const locs = deriveMapLocations(yoinks);
    if (fitToMarkers && locs.length && google.maps.LatLngBounds) {
      const frame = new google.maps.LatLngBounds();
      locs.forEach((loc) => frame.extend({ lat: loc.lat, lng: loc.lng }));
      map.fitBounds(frame);
      // V1 caps the fit at zoom 16 (map.js fitBoundsToMarkers)
      const once = map.addListener('idle', () => {
        if (!destroyed && map.getZoom() > 16) map.setZoom(16);
        once?.remove?.();
      });
    }
    for (const loc of locs) {
      const marker = new google.maps.Marker({
        position: { lat: loc.lat, lng: loc.lng },
        map,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: markerColor(loc, collectedIds),
          fillOpacity: 0.95,
          strokeColor: '#FFFFFF',
          strokeWeight: 2,
          // V1 map.js:394-414: busier spots draw bigger
          scale: Math.min(8 + loc.yoinkCount, 16),
        },
      });
      marker.addListener('click', () => {
        openLocationId = loc.id;
        infoWindow.setContent(infoHtml(loc));
        infoWindow.open(map, marker);
      });
      markers.push(marker);
      // An open popup stays open through idle-refresh rebuilds (V1)
      if (openLocationId === loc.id) {
        infoWindow.setContent(infoHtml(loc));
        infoWindow.open(map, marker);
      }
    }
    if (window.markerClusterer?.MarkerClusterer && markers.length > 20) {
      clusterer = new markerClusterer.MarkerClusterer({
        map,
        markers,
        renderer: {
          render: ({ count, position }) =>
            new google.maps.Marker({
              position,
              icon: {
                path: google.maps.SymbolPath.CIRCLE,
                fillColor: MARKER_COLORS.default,
                fillOpacity: 0.9,
                strokeColor: '#FFFFFF',
                strokeWeight: 2,
                scale: Math.min(20 + Math.log2(count) * 4, 40),
              },
              label: { text: String(count), color: '#FFFFFF', fontSize: '12px', fontWeight: 'bold' },
            }),
        },
      });
    }
  }

  /**
   * Draw a route line (the "On the way" page) and frame the map to it.
   * The line belongs to this mount: cleanup takes it off the shared map.
   */
  function setPath(path) {
    if (destroyed || !path?.length) return;
    routeLine?.setMap(null);
    routeLine = new google.maps.Polyline({
      path,
      strokeColor: '#7a2e2e', // stamp red — reads as inked-on-the-map
      strokeOpacity: 0.75,
      strokeWeight: 4,
      map,
    });
    const bounds = new google.maps.LatLngBounds();
    path.forEach((p) => bounds.extend(p));
    map.fitBounds(bounds, 48);
  }

  return {
    setYoinks,
    setPath,
    cleanup() {
      // The shared map instance deliberately survives — only this
      // mount's listeners, markers, and DOM attachment go away.
      destroyed = true;
      liveHosts.delete(el);
      failedHosts.delete(el);
      routeLine?.setMap(null);
      routeLine = null;
      // The viewport params only mean something while a map is up —
      // a share taken after toggling back to the grid stays clean.
      setUrlParams({ lat: '', lng: '', zoom: '' });
      clearTimeout(boundsTimer);
      el.removeEventListener('click', onClick);
      clearMarkers();
      infoWindow.close?.();
      idleListener?.remove?.();
      if (canvas.parentNode === el) el.removeChild(canvas);
    },
  };
}

/** Test hook: drops the shared map so each test starts clean. */
export function _resetSharedMap() {
  shared = null;
}

// V1 vintage map styling, copied verbatim from assets/js/map.js getMapStyles()
const VINTAGE_STYLES = [
    {
      featureType: 'poi',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }]
    },
    {
      featureType: 'transit',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }]
    },
    {
      featureType: 'water',
      elementType: 'geometry',
      stylers: [{ color: '#a8d5e5' }]
    },
    {
      featureType: 'landscape',
      elementType: 'geometry',
      stylers: [{ color: '#f5f0e6' }]
    },
    {
      featureType: 'road',
      elementType: 'geometry',
      stylers: [{ color: '#ffffff' }]
    },
    {
      featureType: 'road',
      elementType: 'geometry.stroke',
      stylers: [{ color: '#d4c9b9' }]
    }
  ];
